import { Component, inject, signal, viewChild, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { MessageModule } from 'primeng/message';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TabsModule } from 'primeng/tabs';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { AvailableLocation, MerchantDetail, MerchantLocation } from '../../core/models';
import { LocationUsersPanel } from '../location-users/location-users-panel';

// Merchant ids are GUIDs; a location id is anything else. /merchants/:locationId was the old route of
// what is now /locations/:locationId, so an old link lands here and is sent on.
const MERCHANT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LOCATION_ID = /^[A-Za-z0-9._-]{1,100}$/;

// One merchant: its locations (the stores it owns) and its location users (who signs in to them).
// A location may be added before Magento onboards it — the id is what orders, products, queues and
// payments use, so it is chosen here and matched later rather than invented by the platform.
@Component({
    selector: 'app-merchant-page',
    standalone: true,
    imports: [FormsModule, RouterLink, ButtonModule, TableModule, TagModule, MessageModule, DialogModule, InputTextModule,
        SelectModule, SelectButtonModule, TabsModule, ToggleSwitchModule, TooltipModule, LocationUsersPanel],
    template: `
        <div class="flex flex-wrap items-center gap-3 mb-4">
            <p-button icon="pi pi-arrow-left" [text]="true" routerLink="/merchants" />
            <div class="min-w-0">
                <div class="flex items-center gap-2">
                    <h4 class="m-0">{{ merchant()?.name ?? 'Merchant' }}</h4>
                    @if (merchant(); as m) {
                        <p-tag [value]="m.isActive ? 'Active' : 'Inactive'" [severity]="m.isActive ? 'success' : 'secondary'" />
                    }
                </div>
                @if (contactLine(); as contact) {
                    <span class="text-sm text-surface-500">{{ contact }}</span>
                }
            </div>
            @if (merchant()) {
                <div class="flex gap-2 ml-auto">
                    <p-button label="Edit" icon="pi pi-pencil" [text]="true" (onClick)="openEdit()" />
                    <p-button label="Delete merchant" icon="pi pi-trash" severity="danger" [text]="true" (onClick)="openDelete()" />
                </div>
            }
        </div>

        <!-- A failed REFRESH keeps the merchant on screen and shows the failure beside it; only a first load
             with nothing to show is allowed to replace the page. -->
        @if (merchant(); as m) {
            @if (error(); as e) {
                <p-message severity="error" styleClass="mb-3">
                    <span class="mr-2">{{ e }}</span>
                    <p-button label="Try again" size="small" [text]="true" [loading]="loading()" (onClick)="load()" />
                </p-message>
            }
            <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
                <p-tablist>
                    <p-tab value="locations">Locations ({{ m.locations.length }})</p-tab>
                    <p-tab value="users">Users</p-tab>
                </p-tablist>
                <p-tabpanels>
                    <p-tabpanel value="locations">
                        <div class="flex flex-wrap items-center justify-between gap-2 mb-3">
                            <span></span>
                            <p-button label="Add location" icon="pi pi-plus" size="small" (onClick)="openAdd()" />
                        </div>

                        <p-table [value]="m.locations">
                            <ng-template pTemplate="header">
                                <tr>
                                    <th>Location ID</th>
                                    <th>Name</th>
                                    <th>Address</th>
                                    <th>Phone</th>
                                    <th>Status</th>
                                    <th>Magento</th>
                                    <th style="width: 7rem"></th>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-l>
                                <tr>
                                    <td class="font-medium">
                                        @if (l.onboarded) {
                                            <a class="text-primary hover:underline" [routerLink]="['/locations', l.locationId]">{{ l.locationId }}</a>
                                        } @else {
                                            {{ l.locationId }}
                                        }
                                    </td>
                                    <td>{{ l.name }}</td>
                                    <td>{{ l.address ?? '—' }}</td>
                                    <td>{{ l.phone ?? '—' }}</td>
                                    <td><p-tag [value]="l.isActive ? 'Active' : 'Inactive'" [severity]="l.isActive ? 'success' : 'secondary'" /></td>
                                    <td>
                                        @if (l.onboarded) {
                                            <p-tag value="Onboarded" severity="success" />
                                            @if (l.instanceName) {
                                                <span class="block text-xs text-surface-500 mt-1">{{ l.instanceName }}</span>
                                            }
                                        } @else {
                                            <p-tag value="Not onboarded" severity="secondary" />
                                        }
                                    </td>
                                    <td>
                                        <div class="flex justify-end gap-1">
                                            <p-button icon="pi pi-pencil" [text]="true" size="small" ariaLabel="Edit" (onClick)="openEditLocation(l)" />
                                            <p-button icon="pi pi-times" [text]="true" size="small" severity="danger" ariaLabel="Remove from merchant"
                                                      (onClick)="openRemove(l)" />
                                        </div>
                                    </td>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="emptymessage">
                                <tr><td colspan="7">No locations yet — add the merchant's first store.</td></tr>
                            </ng-template>
                        </p-table>
                    </p-tabpanel>

                    <p-tabpanel value="users">
                        @if (visited().has('users')) {
                            <app-location-users-panel [merchantId]="m.merchantId" />
                        }
                    </p-tabpanel>
                </p-tabpanels>
            </p-tabs>
        } @else if (loading()) {
            <p class="text-sm text-surface-500">Loading…</p>
        } @else if (error()) {
            <p-message severity="error">{{ error() }}</p-message>
        }

        <!-- Edit merchant -->
        <p-dialog header="Edit merchant" [(visible)]="editOpen" [modal]="true" [style]="{ width: '32rem' }" [breakpoints]="{ '640px': '95vw' }">
            <div class="flex flex-col gap-3">
                <div class="flex flex-col gap-1">
                    <label class="font-medium">Name</label>
                    <input pInputText [(ngModel)]="form.name" />
                </div>
                <div class="flex flex-col gap-1">
                    <label class="font-medium">Email <span class="text-muted-color">(optional)</span></label>
                    <input pInputText type="email" [(ngModel)]="form.email" />
                </div>
                <div class="flex flex-col gap-1">
                    <label class="font-medium">Phone <span class="text-muted-color">(optional)</span></label>
                    <input pInputText [(ngModel)]="form.phone" />
                </div>
                <div class="flex flex-col gap-1">
                    <label class="font-medium">Address <span class="text-muted-color">(optional)</span></label>
                    <input pInputText [(ngModel)]="form.address" />
                </div>
                <div class="flex items-center gap-2">
                    <p-toggleswitch [(ngModel)]="form.isActive" />
                    <span class="text-sm">Active<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="An inactive merchant is not offered at location sign-in." tooltipPosition="top"></i></span>
                </div>
                @if (formError()) { <p-message severity="error">{{ formError() }}</p-message> }
            </div>
            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" [text]="true" [disabled]="saving()" (onClick)="editOpen.set(false)" />
                <p-button label="Save" icon="pi pi-check" [loading]="saving()" (onClick)="saveMerchant()" />
            </ng-template>
        </p-dialog>

        <!-- Delete merchant -->
        <p-dialog header="Delete merchant" [(visible)]="deleteOpen" [modal]="true" [style]="{ width: '32rem' }" [breakpoints]="{ '640px': '95vw' }">
            <p class="text-sm mb-3">
                Deletes <b>{{ merchant()?.name }}</b> and its location users' access to it; its locations must be removed first.
            </p>
            @if (deleteError()) { <p-message severity="error">{{ deleteError() }}</p-message> }
            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" [text]="true" [disabled]="saving()" (onClick)="deleteOpen.set(false)" />
                <p-button label="Delete merchant" severity="danger" [loading]="saving()" (onClick)="confirmDeleteMerchant()" />
            </ng-template>
        </p-dialog>

        <!-- Add location: an onboarded id waiting for a merchant, or one typed ahead of onboarding -->
        <p-dialog header="Add location" [(visible)]="addOpen" [modal]="true" [style]="{ width: '34rem' }" [breakpoints]="{ '640px': '95vw' }">
            <div class="flex flex-col gap-3">
                <p-selectbutton [options]="addModes" [(ngModel)]="addForm.mode" optionLabel="label" optionValue="value" [allowEmpty]="false" />

                @if (addForm.mode === 'pick') {
                    <div class="flex flex-col gap-1">
                        <label class="font-medium">Onboarded location</label>
                        @if (availableLoading()) {
                            <span class="text-sm text-surface-500">Loading…</span>
                        } @else if (availableError(); as e) {
                            <!-- The list could not be read — which is not the same as there being none. -->
                            <p-message severity="warn">
                                <span class="mr-2">The onboarded locations could not be read: {{ e }} Type an id instead.</span>
                                <p-button label="Try again" size="small" [text]="true" (onClick)="loadAvailable()" />
                            </p-message>
                        } @else if (available().length) {
                            <p-select [options]="available()" [(ngModel)]="addForm.pickedId" optionLabel="locationId" optionValue="locationId"
                                      placeholder="Choose a location" [filter]="true" filterBy="locationId,name" appendTo="body" styleClass="w-full">
                                <ng-template let-a pTemplate="item">
                                    <div class="flex flex-col">
                                        <span>{{ a.name ?? a.locationId }}</span>
                                        <span class="text-xs text-surface-500">{{ a.locationId }}@if (a.instanceName) { · {{ a.instanceName }} }</span>
                                    </div>
                                </ng-template>
                            </p-select>
                        } @else {
                            <p-message severity="info">
                                No onboarded location is waiting for a merchant. Type an id instead.
                            </p-message>
                        }
                    </div>
                } @else {
                    <div class="flex flex-col gap-1">
                        <label class="font-medium">Location id<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Letters, digits, - _ and . only, up to 100 characters. Use the id Magento will onboard the store under." tooltipPosition="top"></i></label>
                        <input pInputText [(ngModel)]="addForm.typedId" placeholder="e.g. local-store" />
                    </div>
                }

                <div class="flex flex-col gap-1">
                    <label class="font-medium">Name @if (!nameRequired()) { <span class="text-muted-color">(optional)</span> }</label>
                    <input pInputText [(ngModel)]="addForm.name" [placeholder]="defaultName() ?? ''" />
                    @if (nameReason(); as reason) { <small class="text-muted-color">{{ reason }}</small> }
                </div>
                <div class="flex flex-col gap-1">
                    <label class="font-medium">Address <span class="text-muted-color">(optional)</span></label>
                    <input pInputText [(ngModel)]="addForm.address" />
                </div>
                <div class="flex flex-col gap-1">
                    <label class="font-medium">Phone <span class="text-muted-color">(optional)</span></label>
                    <input pInputText [(ngModel)]="addForm.phone" />
                </div>
                @if (formError()) { <p-message severity="error">{{ formError() }}</p-message> }
            </div>
            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" [text]="true" [disabled]="saving()" (onClick)="addOpen.set(false)" />
                <p-button label="Add location" icon="pi pi-check" [loading]="saving()" (onClick)="addLocation()" />
            </ng-template>
        </p-dialog>

        <!-- Edit location -->
        <p-dialog header="Edit location" [(visible)]="editLocationOpen" [modal]="true" [style]="{ width: '32rem' }" [breakpoints]="{ '640px': '95vw' }">
            @if (editingLocation(); as l) {
                <div class="flex flex-col gap-3">
                    <div class="text-sm text-surface-500">{{ l.locationId }}</div>
                    <div class="flex flex-col gap-1">
                        <label class="font-medium">Name</label>
                        <input pInputText [(ngModel)]="locationForm.name" />
                        @if (l.magentoStoreName) {
                            <small class="text-muted-color">Magento calls this store "{{ l.magentoStoreName }}".</small>
                        }
                    </div>
                    <div class="flex flex-col gap-1">
                        <label class="font-medium">Address <span class="text-muted-color">(optional)</span></label>
                        <input pInputText [(ngModel)]="locationForm.address" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <label class="font-medium">Phone <span class="text-muted-color">(optional)</span></label>
                        <input pInputText [(ngModel)]="locationForm.phone" />
                    </div>
                    <div class="flex items-center gap-2">
                        <p-toggleswitch [(ngModel)]="locationForm.isActive" />
                        <span class="text-sm">Active<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="An inactive location is not offered at sign-in." tooltipPosition="top"></i></span>
                    </div>
                    @if (formError()) { <p-message severity="error">{{ formError() }}</p-message> }
                </div>
            }
            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" [text]="true" [disabled]="saving()" (onClick)="editLocationOpen.set(false)" />
                <p-button label="Save" icon="pi pi-check" [loading]="saving()" (onClick)="saveLocation()" />
            </ng-template>
        </p-dialog>

        <!-- Remove location from merchant -->
        <p-dialog header="Remove from merchant" [(visible)]="removeOpen" [modal]="true" [style]="{ width: '34rem' }" [breakpoints]="{ '640px': '95vw' }">
            @if (removingLocation(); as l) {
                <p class="text-sm mb-3">
                    Removes <b>{{ l.name }}</b> ({{ l.locationId }}) from <b>{{ merchant()?.name }}</b>; location users lose the
                    access they were granted to it.
                </p>
            }
            @if (formError()) { <p-message severity="error">{{ formError() }}</p-message> }
            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" [text]="true" [disabled]="saving()" (onClick)="removeOpen.set(false)" />
                <p-button label="Remove from merchant" severity="danger" [loading]="saving()" (onClick)="confirmRemove()" />
            </ng-template>
        </p-dialog>
    `
})
export class MerchantPage implements OnInit {
    private readonly api = inject(MiddlewareApi);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly usersPanel = viewChild(LocationUsersPanel);

    merchantId = '';

    readonly merchant = signal<MerchantDetail | null>(null);
    readonly loading = signal(true);
    readonly error = signal<string | null>(null);
    readonly saving = signal(false);
    readonly formError = signal<string | null>(null);

    // The Users tab mounts its panel on first open, so landing here fires the locations request alone.
    readonly activeTab = signal<string>('locations');
    readonly visited = signal<Set<string>>(new Set(['locations']));

    readonly editOpen = signal(false);
    form = { name: '', email: '', phone: '', address: '', isActive: true };

    readonly deleteOpen = signal(false);
    readonly deleteError = signal<string | null>(null);

    readonly addOpen = signal(false);
    readonly addModes = [{ label: 'Onboarded location', value: 'pick' }, { label: 'New location id', value: 'type' }];
    readonly available = signal<AvailableLocation[]>([]);
    readonly availableLoading = signal(false);
    // Kept apart from formError: an empty list and an unread one are different sentences, and neither is
    // a refusal of the form.
    readonly availableError = signal<string | null>(null);
    addForm: { mode: 'pick' | 'type'; pickedId: string | null; typedId: string; name: string; address: string; phone: string } = this.blankAddForm();

    readonly editLocationOpen = signal(false);
    readonly editingLocation = signal<MerchantLocation | null>(null);
    locationForm = { name: '', address: '', phone: '', isActive: true };

    readonly removeOpen = signal(false);
    readonly removingLocation = signal<MerchantLocation | null>(null);

    ngOnInit(): void {
        // paramMap rather than the snapshot: a link from one merchant to another reuses this component.
        this.route.paramMap.subscribe((params) => {
            const id = params.get('merchantId') ?? '';
            // An old /merchants/<locationId> link: that page is now /locations/<locationId>.
            if (id && !MERCHANT_ID.test(id)) {
                this.router.navigate(['/locations', id], { replaceUrl: true });
                return;
            }
            this.merchantId = id;
            this.merchant.set(null);
            this.activeTab.set('locations');
            this.visited.set(new Set(['locations']));
            this.load();
        });
    }

    onTabChange(value: string | number | undefined): void {
        const tab = String(value ?? 'locations');
        this.activeTab.set(tab);
        if (!this.visited().has(tab)) this.visited.update((s) => new Set(s).add(tab));
    }

    contactLine(): string | null {
        const m = this.merchant();
        if (!m) return null;
        const parts = [m.email, m.phone, m.address].filter((p): p is string => !!p);
        return parts.length ? parts.join(' · ') : null;
    }

    openEdit(): void {
        const m = this.merchant();
        if (!m) return;
        this.form = { name: m.name, email: m.email ?? '', phone: m.phone ?? '', address: m.address ?? '', isActive: m.isActive };
        this.formError.set(null);
        this.editOpen.set(true);
    }

    saveMerchant(): void {
        const name = this.form.name.trim();
        if (!name) { this.formError.set('Name is required.'); return; }
        this.saving.set(true);
        this.formError.set(null);
        this.api.updateMerchant(this.merchantId, {
            name,
            email: this.form.email.trim() || null,
            phone: this.form.phone.trim() || null,
            address: this.form.address.trim() || null,
            isActive: this.form.isActive
        }).subscribe({
            next: (summary) => {
                this.saving.set(false);
                this.editOpen.set(false);
                this.merchant.update((m) => (m ? { ...m, ...summary } : m));
            },
            error: (e) => { this.saving.set(false); this.formError.set(this.describe(e, 'merchants:manage')); }
        });
    }

    openDelete(): void {
        this.deleteError.set(null);
        this.deleteOpen.set(true);
    }

    confirmDeleteMerchant(): void {
        this.saving.set(true);
        this.deleteError.set(null);
        this.api.deleteMerchant(this.merchantId).subscribe({
            next: () => { this.saving.set(false); this.deleteOpen.set(false); this.router.navigate(['/merchants']); },
            // 409 while it still has locations — the API's sentence says so.
            error: (e) => { this.saving.set(false); this.deleteError.set(this.describe(e, 'merchants:manage')); }
        });
    }

    openAdd(): void {
        this.addForm = this.blankAddForm();
        this.formError.set(null);
        this.addOpen.set(true);
        this.loadAvailable();
    }

    loadAvailable(): void {
        this.availableLoading.set(true);
        this.availableError.set(null);
        this.api.getAvailableLocations().subscribe({
            next: (a) => { this.available.set(a ?? []); this.availableLoading.set(false); },
            error: (e) => { this.available.set([]); this.availableLoading.set(false); this.availableError.set(this.describe(e, 'merchants:read')); }
        });
    }

    /** The onboarded store name the API would take when no name is given. */
    defaultName(): string | null {
        return this.pickedAvailable()?.name ?? null;
    }

    /** A name is required unless the API has one to default from. Being onboarded is not enough: the store's
     *  name is null until its details have been fetched from Magento. */
    nameRequired(): boolean {
        return !this.defaultName();
    }

    /** Why the name has to be typed, once there is an id to say it about. */
    nameReason(): string | null {
        if (!this.nameRequired() || !this.chosenId()) return null;
        return this.pickedAvailable()
            ? 'This store has no name yet — its details have not been fetched from Magento. Fetch them on the Locations page, or give a name here.'
            : 'This id is not an onboarded store, so there is no name to take from Magento.';
    }

    addLocation(): void {
        const locationId = this.chosenId();
        if (!locationId) { this.formError.set(this.addForm.mode === 'pick' ? 'Choose a location.' : 'A location id is required.'); return; }
        if (!LOCATION_ID.test(locationId)) {
            this.formError.set('A location id is 1-100 characters of letters, digits, - _ or . only.');
            return;
        }
        const name = this.addForm.name.trim();
        if (!name && this.nameRequired()) {
            this.formError.set(`A name is required. ${this.nameReason()}`);
            return;
        }
        this.saving.set(true);
        this.formError.set(null);
        this.api.addMerchantLocation(this.merchantId, {
            locationId,
            name: name || null,
            address: this.addForm.address.trim() || null,
            phone: this.addForm.phone.trim() || null
        }).subscribe({
            next: () => { this.saving.set(false); this.addOpen.set(false); this.afterLocationsChanged(); },
            error: (e) => { this.saving.set(false); this.formError.set(this.describe(e, 'merchants:manage')); }
        });
    }

    openEditLocation(l: MerchantLocation): void {
        this.editingLocation.set(l);
        this.locationForm = { name: l.name, address: l.address ?? '', phone: l.phone ?? '', isActive: l.isActive };
        this.formError.set(null);
        this.editLocationOpen.set(true);
    }

    saveLocation(): void {
        const l = this.editingLocation();
        if (!l) return;
        const name = this.locationForm.name.trim();
        if (!name) { this.formError.set('Name is required.'); return; }
        this.saving.set(true);
        this.formError.set(null);
        this.api.updateMerchantLocation(this.merchantId, l.locationId, {
            name,
            address: this.locationForm.address.trim() || null,
            phone: this.locationForm.phone.trim() || null,
            isActive: this.locationForm.isActive
        }).subscribe({
            next: () => { this.saving.set(false); this.editLocationOpen.set(false); this.afterLocationsChanged(); },
            error: (e) => { this.saving.set(false); this.formError.set(this.describe(e, 'merchants:manage')); }
        });
    }

    openRemove(l: MerchantLocation): void {
        this.removingLocation.set(l);
        this.formError.set(null);
        this.removeOpen.set(true);
    }

    confirmRemove(): void {
        const l = this.removingLocation();
        if (!l) return;
        this.saving.set(true);
        this.formError.set(null);
        this.api.removeMerchantLocation(this.merchantId, l.locationId).subscribe({
            next: () => { this.saving.set(false); this.removeOpen.set(false); this.afterLocationsChanged(); },
            error: (e) => { this.saving.set(false); this.formError.set(this.describe(e, 'merchants:manage')); }
        });
    }

    /** The id the dialog is working on, whichever mode it is in. */
    private chosenId(): string {
        return (this.addForm.mode === 'pick' ? this.addForm.pickedId ?? '' : this.addForm.typedId).trim();
    }

    private pickedAvailable(): AvailableLocation | null {
        const id = this.chosenId();
        return id ? this.available().find((a) => a.locationId === id) ?? null : null;
    }

    /** Public so a failed refresh can be retried from the banner it put on screen. */
    load(): void {
        if (!this.merchantId) return;
        this.loading.set(true);
        this.error.set(null);
        this.api.getMerchant(this.merchantId).subscribe({
            next: (m) => { this.merchant.set(m); this.loading.set(false); },
            error: (e) => {
                this.error.set(e?.status === 404 && !e?.error?.error ? 'That merchant does not exist.' : this.describe(e, 'merchants:read'));
                this.loading.set(false);
            }
        });
    }

    /** A location moving in or out changes the users' access chips too, so the panel is re-read with it. */
    private afterLocationsChanged(): void {
        this.load();
        this.usersPanel()?.reload();
    }

    private describe(e: any, permission: string): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        // 400/404/409 carry { error }: a sentence written for the operator.
        if (e?.error?.error) return e.error.error;
        if (e?.status === 401) return 'Unauthorized — signing in…';
        if (e?.status === 403) return `Forbidden — your role lacks the ${permission} permission.`;
        return `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }

    private blankAddForm() {
        return { mode: 'pick' as const, pickedId: null, typedId: '', name: '', address: '', phone: '' };
    }
}
